import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';

export default function BuyerSignup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [validating, setValidating] = useState(true);
  const [invite, setInvite] = useState(null);
  const [validationError, setValidationError] = useState('');

  const [form, setForm] = useState({ full_name: '', email: '', password: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [signupError, setSignupError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/invites/validate/${encodeURIComponent(code)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!data.valid) {
          setValidationError(data.message || 'Invite link is not valid.');
          setInvite(data);
        } else {
          setInvite(data);
          setForm((prev) => ({
            ...prev,
            full_name: data.buyer_name || '',
            email: data.buyer_email || '',
          }));
        }
      } catch (err) {
        if (cancelled) return;
        setValidationError('Could not reach the server. Please try again later.');
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (form.password.length < 8) {
      setSignupError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/buyer-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_code: code,
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          password: form.password,
          phone: form.phone.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSignupError(data.detail || 'Could not create your account.');
        setSubmitting(false);
        return;
      }
      // Use the existing login flow to hydrate AuthContext, then send the buyer to set up criteria.
      try {
        await login(form.email.trim(), form.password);
      } catch (err) {
        setSignupError('Account created, but auto-login failed. Please log in manually.');
        setSubmitting(false);
        return;
      }
      navigate(`/buyers/${data.buyer_id}/criteria`, { replace: true });
    } catch (err) {
      setSignupError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4">
        <div className="text-[#00D4FF] text-lg">Checking your invite...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            Scheme<span className="text-[#00D4FF]">Sync</span>
          </h1>
          <p className="text-[#00E676] font-semibold mt-1 text-lg">Commercial Real Estate</p>
        </div>

        <div className="bg-[#1A2744] rounded-xl shadow-2xl p-6 md:p-8 border border-[#546E7A]/30">
          {validationError ? (
            <>
              <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-4 rounded-lg mb-4">
                <p className="font-semibold mb-1">Invite unavailable</p>
                <p className="text-sm text-red-300/80">{validationError}</p>
              </div>
              <Link to="/" className="block text-[#00D4FF] hover:text-[#00E676] text-sm text-center transition-colors">
                ← Back to sign in
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-2">Create your account</h2>
              {invite?.agent_name && invite?.brokerage_name && (
                <p className="text-[#B0BEC5] text-sm mb-6">
                  You've been invited by <span className="text-[#00D4FF] font-medium">{invite.agent_name}</span> at{' '}
                  <span className="text-white font-medium">{invite.brokerage_name}</span>.
                </p>
              )}

              {signupError && (
                <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
                  {signupError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full name</label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent"
                    placeholder="Pinnacle Medical Group"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent"
                    placeholder="you@email.com"
                    required
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Phone <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent"
                    placeholder="(321) 555-0142"
                    autoComplete="tel"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 px-4 bg-[#00E676] hover:bg-[#00C853] disabled:bg-[#00E676]/50 disabled:cursor-not-allowed text-[#0A0E27] font-semibold rounded-lg transition-colors duration-200"
                >
                  {submitting ? 'Creating account...' : 'Create Account'}
                </button>
              </form>

              <p className="text-gray-500 text-xs text-center mt-6">
                By creating an account you'll get access to your personalized property match board.
              </p>
            </>
          )}
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          &copy; 2026 AiDA Advisory LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}
